import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_FULL = "/logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function Footer() {
  const { t } = useLanguage();
  const [sc, setSc] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setSc(r.data || {})).catch(() => {});
  }, []);

  const phones = (sc.phones && sc.phones.length ? sc.phones : [sc.phone || '+1 548-532-2279']).filter(Boolean);
  const addresses = (sc.addresses && sc.addresses.length ? sc.addresses : [sc.address || '275 Gardenbrooke Trail, Brampton, ON L6P 3L1']).filter(Boolean);
  const email = sc.email || 'info@drrenuclinic.com';
  const whatsapp = sc.whatsapp_number || '15485322279';
  const oh = sc.opening_hours || {};
  const weekdayHours = oh.Monday || '8:00 AM - 6:00 PM';
  const saturdayHours = oh.Saturday || '9:00 AM - 2:00 PM';
  const sundayHours = oh.Sunday || 'Closed';

  return (
    <footer className="bg-foreground text-white" data-testid="main-footer">
      {/* Newsletter bar */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-6 md:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl font-medium">Stay Connected</h3>
              <p className="text-sm text-white/50 mt-1">Get health tips and clinic updates</p>
            </div>
            <div className="flex gap-3">
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="footer-whatsapp-btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </Button>
              </a>
              <a href={`tel:${phones[0].replace(/[^+\d]/g, '')}`}>
                <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="footer-call-btn">
                  <Phone className="w-4 h-4 mr-2" /> Call Clinic
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_FULL} alt="Dr. Reenu Women's Clinic" className="h-16 mb-5 object-contain" />
            <p className="text-white/50 text-sm leading-relaxed">
              Providing compassionate and confidential women's healthcare services. Your health, privacy, and comfort are our top priorities.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5">{t('footer.quickLinks')}</h4>
            <div className="space-y-3">
              {[{ to: '/', label: t('nav.home') }, { to: '/about', label: t('nav.about') }, { to: '/products', label: t('nav.products') }, { to: '/gynecology', label: t('nav.gynecology') }, { to: '/abortion', label: t('nav.abortion') }, { to: '/blog', label: t('nav.blog') }, { to: '/contact', label: t('nav.contact') }].map(link => (
                <Link key={link.to} to={link.to} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors group" data-testid={`footer-link-${link.to.slice(1) || 'home'}`}>
                  <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5">{t('nav.products')}</h4>
            <div className="space-y-3">
              {['Unwanted Kit', 'Mifegymiso', 'Gestapo', 'Misoprostol'].map(p => (
                <Link key={p} to={`/products?filter=${p}`} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors group" data-testid={`footer-product-${p.toLowerCase().replace(/\s/g, '-')}`}>
                  <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  {p}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact (all dynamic from admin, supports multiple phones/addresses) */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5">{t('footer.contactInfo')}</h4>
            <div className="space-y-4">
              {addresses.map((addr, i) => (
                <div key={`addr-${i}`} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <span className="text-sm text-white/50" data-testid={`footer-address-${i}`}>{addr}</span>
                </div>
              ))}
              {phones.map((ph, i) => (
                <a key={`ph-${i}`} href={`tel:${ph.replace(/[^+\d]/g, '')}`} className="flex items-center gap-3 hover:text-white group">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="text-sm text-white/50 group-hover:text-white transition-colors" data-testid={`footer-phone-${i}`}>{ph}</span>
                </a>
              ))}
              <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-white group">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <Mail className="w-4 h-4" />
                </div>
                <span className="text-sm text-white/50 group-hover:text-white transition-colors break-all" data-testid="footer-email">{email}</span>
              </a>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="text-sm text-white/50" data-testid="footer-hours">
                  <p>Mon-Fri: {weekdayHours}</p>
                  <p>Sat: {saturdayHours}</p>
                  <p>Sun: {sundayHours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/30">&copy; {new Date().getFullYear()} Dr. Reenu Women's Clinic. {t('footer.rights')}</p>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-white/30 hover:text-white/60 transition-colors" data-testid="footer-privacy">{t('footer.privacy')}</a>
            <a href="#" className="text-sm text-white/30 hover:text-white/60 transition-colors" data-testid="footer-terms">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
